import { useState, useEffect, useRef } from 'react';
import { getMessageList, getMessageDetail, deleteMessage } from '../services/api';

const LIMIT = 20;

/**
 * Message list, paging and the selected message for MessagesPanel.
 *
 * Fetches the first page when `isOpen` becomes true and clears the selected
 * message when it becomes false.
 *
 * @param {boolean} isOpen - whether the panel is open
 */
export const useMessages = (isOpen) => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const messagesFetchedRef = useRef(false);

  // Fetch messages when panel opens
  useEffect(() => {
    if (isOpen && !messagesFetchedRef.current) {
      fetchMessages(0);
      messagesFetchedRef.current = true;
    }

    // Reset when panel closes
    if (!isOpen) {
      messagesFetchedRef.current = false;
      setSelectedMessage(null);
    }
  }, [isOpen]);

  const fetchMessages = async (skipCount) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMessageList(skipCount, LIMIT);

      if (skipCount === 0) {
        // Initial load
        setMessages(data.messages);
      } else {
        // Pagination - append
        setMessages(prev => [...prev, ...data.messages]);
      }

      setTotal(data.total);
      setSkip(skipCount);
      setHasMore(skipCount + data.messages.length < data.total);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchMessages(skip + LIMIT);
    }
  };

  const openMessage = async (messageKey) => {
    try {
      setLoading(true);
      setError(null);
      const messageData = await getMessageDetail(messageKey);
      setSelectedMessage(messageData);

      // Update the message in the list to mark it as read
      setMessages(prev =>
        prev.map(msg =>
          msg.message_key === messageKey
            ? { ...msg, read: true }
            : msg
        )
      );
    } catch (err) {
      console.error('Error fetching message detail:', err);
      setError('Failed to load message');
    } finally {
      setLoading(false);
    }
  };

  // Callers confirm with the user before calling this
  const removeMessage = async (messageKey) => {
    try {
      setError(null);
      await deleteMessage(messageKey);

      // Remove message from list
      setMessages(prev => prev.filter(msg => msg.message_key !== messageKey));

      // If we're viewing this message, go back to list
      if (selectedMessage?.message_key === messageKey) {
        setSelectedMessage(null);
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
    }
  };

  const clearSelectedMessage = () => {
    setSelectedMessage(null);
  };

  return {
    messages,
    selectedMessage,
    loading,
    error,
    total,
    hasMore,
    loadMore,
    openMessage,
    removeMessage,
    clearSelectedMessage,
  };
};

export default useMessages;
